import Alert from '@/components/Alert/Alert'
import TableView, { TableRow, TableSection } from '@/components/TableView'
import { useIsAppReviewMode } from '@/lib/hooks/useIsAppReviewMode'
import { isPlanCodeGtOther } from '@/lib/payment/plans'
import { CurrentUser, fetchCurrentUserThunk } from '@/redux/slices/currentUserSlice'
import { fetchTogglesThunk, switchUserThunk } from '@/redux/slices/sessionSlice'
import { resetStore, useAppDispatch } from '@/redux/store'
import { FeatureTrialStatus, FeatureTrialType, PlanCode, UserRole, UserStatus } from '@/typings/domain'
import { isFulfilled } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import { View } from 'react-native'

type Props = {
  currentUser: CurrentUser
  onSwitchSuccess?: () => void
}

export default function SwitchUser({ currentUser, onSwitchSuccess }: Props) {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const isAppReviewMode = useIsAppReviewMode()

  const handleItemPress = async (selectedUserId: string) => {
    const user = currentUser.billingAccountUsers.find(user => user.id === selectedUserId)
    if (user?.apiToken) {
      // FIXME: need to show a loading indicator
      const result = await dispatch(switchUserThunk({ userToken: user.apiToken }))
      if (isFulfilled(result)) {
        dispatch(resetStore())
        await dispatch(fetchCurrentUserThunk())
        dispatch(fetchTogglesThunk())
        if (router.canDismiss()) {
          router.dismissAll()
        } else {
          router.replace('/forms')
        }
        onSwitchSuccess?.()
      }
    } else {
      Alert.alert('切换失败', '账号或者 Token 不存在')
    }
  }

  // 分离可用和不可用的团队
  const availableRows: TableRow[] = []
  const unavailableRows: TableRow[] = []

  currentUser.billingAccountUsers.forEach(user => {
    const currentFeatureTrialInfo = user.billingAccount.currentFeatureTrialInfo

    const isActiveTrial =
      !!currentFeatureTrialInfo?.trialType &&
      currentFeatureTrialInfo.status === FeatureTrialStatus.InUse &&
      dayjs(currentFeatureTrialInfo.endDate).endOf('day').isAfter(dayjs())

    const currentFeatureTrialType = currentFeatureTrialInfo?.trialType

    const isE30Trial = isActiveTrial && currentFeatureTrialType === FeatureTrialType.E30Trial
    const isEntPlanActiveTrial = isActiveTrial && currentFeatureTrialType === FeatureTrialType.Ent2Trial
    const currentPlanCode = user.billingAccount.planPackage?.plan.code

    const isDisabled = user.status === UserStatus.Disabled
    const isE30Expired =
      user.role !== UserRole.Owner &&
      !isPlanCodeGtOther(currentPlanCode as PlanCode, PlanCode.Ent2) &&
      !isActiveTrial &&
      currentFeatureTrialType === FeatureTrialType.E30Trial
    const isEntPlanExpired =
      user.role !== UserRole.Owner &&
      currentPlanCode === PlanCode.Free &&
      !isActiveTrial &&
      currentFeatureTrialType === FeatureTrialType.Ent2Trial
    const isExpired = isE30Expired || isEntPlanExpired
    const canSwitch = !isDisabled && !isExpired
    const isUsersQuotaExceeded =
      user.role !== UserRole.Owner && user.billingAccount.occupiedUsersCount > user.billingAccount.usersQuota

    let subTitle = ''
    if (!isAppReviewMode) {
      if (isDisabled) {
        subTitle = '账号被禁用'
      } else if (isE30Expired) {
        subTitle = '企业套餐已过期'
      } else if (isEntPlanExpired) {
        subTitle = '个人套餐已过期'
      } else if (isUsersQuotaExceeded) {
        subTitle = '成员数量超出配额'
      } else if (isE30Trial) {
        subTitle = `企业版试用中（${user.billingAccount.currentFeatureTrialInfo?.endDate} 到期）`
      } else if (isEntPlanActiveTrial) {
        subTitle = `个人版试用中（${user.billingAccount.currentFeatureTrialInfo?.endDate} 到期）`
      } else {
        subTitle =
          `${user.billingAccount.planPackage?.plan.label}` +
          (user.billingAccount.planPackage?.endDate ? `（${user.billingAccount.planPackage?.endDate} 到期）` : '')
      }
    }

    const row: TableRow = {
      title: user.billingAccount.shortName || '未命名企业',
      subTitle,
      onPress: canSwitch ? () => handleItemPress(user.id) : undefined,
    }

    if (canSwitch) {
      availableRows.push(row)
    } else {
      unavailableRows.push(row)
    }
  })

  // 构建 sections
  const sections: TableSection[] = []
  if (availableRows.length > 0) {
    sections.push({ title: ' ', rows: availableRows })
  }
  if (unavailableRows.length > 0) {
    sections.push({ title: '不可用团队', rows: unavailableRows })
  }

  return (
    <View style={{ flex: 1 }}>
      <TableView sections={sections} />
    </View>
  )
}
